import type { ReactNode } from 'react'
import { Activity, Crosshair, Globe2, TrendingUp } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { displayMapper } from '@/lib/display-mapper'
import type { DashboardSummaryDto, StatsResponse } from './types'

/**
 * "Today's ransomware brief" — four editorial cells over the trailing 7-day
 * landscape. Every figure is real: activity from `/leak-sites/stats`
 * (this-week vs prior-week), and top group / sector / region from
 * `/leak-sites/dashboard-summary`. A cell with no backing data degrades to a
 * quiet "no activity" line — never an invented number, never a fake
 * week-over-week percentage when the prior window is unknown.
 */

const KICKER_TONES = {
  spike: 'text-rose-700 dark:text-rose-300',
  mover: 'text-amber-700 dark:text-amber-300',
  sector: 'text-violet-700 dark:text-violet-300',
  region: 'text-sky-700 dark:text-sky-300',
  neutral: 'text-muted-foreground',
} as const

type Tone = keyof typeof KICKER_TONES

const fmt = (n: number) => n.toLocaleString('en-GB')

function CellShell({ children }: { children: ReactNode }) {
  return <div className="min-w-0 bg-background p-4 sm:p-5">{children}</div>
}

function Kicker({
  tone,
  icon,
  label,
}: {
  tone: Tone
  icon: ReactNode
  label: string
}) {
  return (
    <span
      className={cn(
        'flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em]',
        KICKER_TONES[tone],
      )}
    >
      {icon}
      {label}
    </span>
  )
}

function Cell({
  tone,
  icon,
  kicker,
  title,
  meta,
}: {
  tone: Tone
  icon: ReactNode
  kicker: string
  title: ReactNode
  meta?: ReactNode
}) {
  return (
    <CellShell>
      <Kicker tone={tone} icon={icon} label={kicker} />
      <p className="mt-2 font-heading text-[15px] font-semibold leading-snug text-foreground">
        {title}
      </p>
      {meta ? (
        <p className="mt-2 text-xs text-muted-foreground">{meta}</p>
      ) : null}
    </CellShell>
  )
}

function CellSkeleton({ label }: { label: string }) {
  return (
    <CellShell>
      <div role="status" aria-label={`Loading ${label}`}>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-3 h-4 w-full" />
        <Skeleton className="mt-1.5 h-4 w-3/4" />
        <Skeleton className="mt-3 h-3 w-28" />
      </div>
    </CellShell>
  )
}

export interface LeakSitesHighlightsBandProps {
  stats?: StatsResponse
  /** Total claims in the prior 7-day window (8–14 days ago), or null when unknown. */
  priorWeekClaims?: number | null
  summary?: DashboardSummaryDto
  isLoading: boolean
}

export function LeakSitesHighlightsBand({
  stats,
  priorWeekClaims,
  summary,
  isLoading,
}: LeakSitesHighlightsBandProps) {
  return (
    <div className="grid grid-cols-1 gap-px border-y border-border bg-border sm:grid-cols-2 xl:grid-cols-4">
      <ActivitySpikeCell
        stats={stats}
        priorWeekClaims={priorWeekClaims}
        isLoading={isLoading}
      />
      <TopGroupCell summary={summary} isLoading={isLoading} />
      <TopSectorCell summary={summary} isLoading={isLoading} />
      <TopRegionCell summary={summary} isLoading={isLoading} />
    </div>
  )
}

function ActivitySpikeCell({
  stats,
  priorWeekClaims,
  isLoading,
}: {
  stats?: StatsResponse
  priorWeekClaims?: number | null
  isLoading: boolean
}) {
  if (isLoading) return <CellSkeleton label="activity" />
  const week = stats?.totals.last7d
  if (week === undefined) {
    return (
      <Cell
        tone="neutral"
        icon={<Activity className="h-3 w-3" aria-hidden="true" />}
        kicker="Activity"
        title="Activity unavailable right now."
      />
    )
  }
  const hasPrior = typeof priorWeekClaims === 'number'
  const diff = hasPrior ? week - priorWeekClaims : null
  const pct = hasPrior && priorWeekClaims !== 0
    ? Math.round(((week - priorWeekClaims) / priorWeekClaims) * 100)
    : null

  return (
    <Cell
      tone="spike"
      icon={<Activity className="h-3 w-3" aria-hidden="true" />}
      kicker="Activity this week"
      title={
        <>
          {fmt(week)} ransomware {week === 1 ? 'claim' : 'claims'} posted in the
          last 7 days
          {pct !== null ? (
            <span className={pct >= 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}>
              {' '}
              {pct >= 0 ? '+' : ''}
              {pct}% w/w
            </span>
          ) : null}
        </>
      }
      meta={
        diff !== null
          ? `${diff >= 0 ? '+' : ''}${fmt(diff)} vs prior 7 days (${fmt(priorWeekClaims!)})`
          : 'Prior-window comparison unavailable'
      }
    />
  )
}

function TopGroupCell({
  summary,
  isLoading,
}: {
  summary?: DashboardSummaryDto
  isLoading: boolean
}) {
  if (isLoading) return <CellSkeleton label="most active group" />
  const g = summary?.topGroup
  if (!g) {
    return (
      <Cell
        tone="neutral"
        icon={<TrendingUp className="h-3 w-3" aria-hidden="true" />}
        kicker="Most active group"
        title="No group claims in the last 7 days."
      />
    )
  }
  return (
    <Cell
      tone="mover"
      icon={<TrendingUp className="h-3 w-3" aria-hidden="true" />}
      kicker="Most active group"
      title={
        <>
          <span className="font-mono">{g.name}</span> leads with {fmt(g.count)}{' '}
          {g.count === 1 ? 'claim' : 'claims'} this week
        </>
      }
      meta="Ranked by named victims in the last 7 days"
    />
  )
}

function TopSectorCell({
  summary,
  isLoading,
}: {
  summary?: DashboardSummaryDto
  isLoading: boolean
}) {
  if (isLoading) return <CellSkeleton label="sector under pressure" />
  const s = summary?.topSector
  if (!s) {
    return (
      <Cell
        tone="neutral"
        icon={<Crosshair className="h-3 w-3" aria-hidden="true" />}
        kicker="Sector under pressure"
        title="No sector signal in the last 7 days."
      />
    )
  }
  return (
    <Cell
      tone="sector"
      icon={<Crosshair className="h-3 w-3" aria-hidden="true" />}
      kicker="Sector under pressure"
      title={
        <>
          {displayMapper.sector(s.sector)} leads with {fmt(s.count)} named{' '}
          {s.count === 1 ? 'victim' : 'victims'}
        </>
      }
      meta={
        summary?.scopedToUserPrefs
          ? 'Scoped to your saved sectors'
          : 'Across all tracked sectors'
      }
    />
  )
}

function TopRegionCell({
  summary,
  isLoading,
}: {
  summary?: DashboardSummaryDto
  isLoading: boolean
}) {
  if (isLoading) return <CellSkeleton label="top region" />
  const c = summary?.topCountry
  if (!c) {
    return (
      <Cell
        tone="neutral"
        icon={<Globe2 className="h-3 w-3" aria-hidden="true" />}
        kicker="Top region"
        title="No regional signal in the last 7 days."
      />
    )
  }
  return (
    <Cell
      tone="region"
      icon={<Globe2 className="h-3 w-3" aria-hidden="true" />}
      kicker="Top region"
      title={
        <>
          {displayMapper.country(c.country)} leads with {fmt(c.count)}{' '}
          {c.count === 1 ? 'claim' : 'claims'}
        </>
      }
      meta={
        summary?.scopedToUserPrefs
          ? 'Scoped to your saved regions'
          : 'Across all tracked regions'
      }
    />
  )
}
